<template>
  <FormElement
    :modelValue="modelValue"
    :required="required"
    @error="hasError = $event"
  >
    <div
      :class="[
        $style.base,
        {
          [$style.hasError]: hasError,
          [$style.isChecked]: !!modelValue,
          [$style.hasDescription]: !!description,
          [$style.isDisabled]: !!disabled
        }
      ]"
      v-test="'_base-checkbox'"
    >
      <div
        :class="$style.checkbox"
        v-test="'_base-checkbox-content'"
      >
        <div
          :class="$style.customCheckbox"
          :data-checked="modelValue"
          @click.stop.prevent="toggleCheckbox"
          v-test="'_checkbox'"
        >
          <BaseIcon
            name="check"
            color="inherit"
            size="s"
          />
        </div>
        <div
          v-show="label"
          :class="[$style.label, { [$style.fixedTextColor]: color === 'fixed' }]"
          @click.stop.prevent="toggleCheckbox"
          v-test="'_checkbox-label'"
        >
          {{ label }}
        </div>
        <div
          v-if="info"
          :class="$style.infoIcon"
        >
          <BaseIcon
            name="info"
            size="s"
            :tooltip="{
            text: info,
            touch: true
          }"
          />
        </div>
      </div>
      <div
        v-if="description"
        :class="[$style.description, { [$style.fixedTextColor]: color === 'fixed' }]"
      >
        {{ description }}
      </div>
    </div>
  </FormElement>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import FormElement from '@/components/_shared/form-element/index.vue';

export default defineComponent({
  components: {
    FormElement,
  },
  inheritAttrs: false,
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    label: {
      type: String,
      required: false,
    },
    description: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: 'default',
    },
    info: String,
  },
  emits: ['update:modelValue'],
  data() {
    return {
      hasError: false,
    };
  },
  methods: {
    toggleCheckbox() {
      this.$emit('update:modelValue', !this.modelValue);
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.input {
  display: none;
}

.checkbox {
  display: flex;
  align-items: center;

  .base.isDisabled & {
    pointer-events: none;
  }
}

.infoIcon {
  display: flex;
  align-items: center;
  margin-left: $spacing * 0.25;
}

.customCheckbox {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-primary);
  border: 1px solid var(--border-strong);
  height: 20px;
  width: 20px;
  border-radius: 5px;
  cursor: default;
  color: var(--text-on-selection);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  .base:hover & {
    border-color: var(--selection);
  }

  .base:active & {
    border-color: var(--selection-hover);
  }

  .base.isChecked & {
    background-color: var(--selection);
    border-color: var(--selection);
  }

  .base:not(.isChecked) & {
    & > * {
      opacity: 0;
    }
  }

  .base.hasError & {
    border-color: var(--error-base);
  }

  .base.isDisabled & {
    background-color: var(--neutral-softer);
    border-color: var(--border);
  }

  // A white tick on the soft neutral is barely visible, so a disabled checked box keeps it dark
  .base.isDisabled.isChecked & {
    background-color: var(--neutral-soft);
    border-color: var(--neutral-soft);
    color: var(--neutral-dark);
  }
}

.label {
  padding-left: $spacing * 0.5;
  line-height: 20px;
  cursor: default;

  .base.hasDescription & {
    font-weight: bold;
  }

  .base.isDisabled & {
    color: var(--text-placeholder);
  }
}

.fixedTextColor {
  color: var(--color-text-fixed);
}

// Lines up under the label: 20px box + 12px gap
.description {
  margin-top: 2px;
  padding-left: 32px;
  line-height: 20px;
  color: var(--text-secondary);

  .base.isDisabled & {
    color: var(--text-placeholder);
  }
}
</style>
